import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell
} from 'recharts';
import * as XLSX from 'xlsx';

const Analytics = () => {
    const { user } = useAuth();
    const { showToast } = useUI();
    const [period, setPeriod] = useState('month');
    const [summary, setSummary] = useState(null);
    const [chartData, setChartData] = useState({ movements: [], categories: [] });
    const [topMovers, setTopMovers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showFullList, setShowFullList] = useState(false);

    const fetchData = useCallback(async () => {
        if (!user?.companyId) return;
        setLoading(true);
        try {
            const [sumRes, chartRes, topRes] = await Promise.all([
                fetch(`/api/analytics/summary?companyId=${user.companyId}&period=${period}`),
                fetch(`/api/analytics/charts?companyId=${user.companyId}&period=${period}`),
                fetch(`/api/analytics/top-movers?companyId=${user.companyId}&period=${period}`)
            ]);

            if (sumRes.ok) setSummary(await sumRes.json());
            if (chartRes.ok) setChartData(await chartRes.json());
            if (topRes.ok) setTopMovers(await topRes.json());
        } catch (error) {
            showToast("Erreur lors du chargement des analyses", "error");
        } finally {
            setLoading(false);
        }
    }, [user?.companyId, period, showToast]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // eslint-disable-next-line no-unused-vars
    const handleExportReport = () => {
        try {
            showToast("Génération du rapport d'analyse...", "info");

            // 1. KPIs
            const kpiData = [
                { 'Indicateur': 'Valeur Totale Stock', 'Valeur': `${Number(summary?.totalStockValue || 0).toLocaleString()} €`, 'Tendance': summary?.trends.value },
                { 'Indicateur': 'Taux de Rotation', 'Valeur': summary?.turnoverRatio || "0x", 'Tendance': summary?.trends.rotation },
                { 'Indicateur': 'Articles en Rupture', 'Valeur': summary?.lowStockAlerts || 0, 'Tendance': summary?.trends.alerts },
                { 'Indicateur': 'Précision d\'Inventaire', 'Valeur': summary?.inventoryPrecision || "100%", 'Tendance': summary?.trends.precision }
            ];

            // 2. Top Movers
            const topMoversData = topMovers.map(item => ({
                'Article': item.name,
                'SKU': item.sku,
                'Catégorie': item.category,
                'Mouvements': item.movements,
                'Stock Actuel': item.stock,
                'Statut': item.status
            }));

            const wb = XLSX.utils.book_new();

            const wsKPIs = XLSX.utils.json_to_sheet(kpiData);
            XLSX.utils.book_append_sheet(wb, wsKPIs, 'Indicateurs Clés');

            const wsTopMovers = XLSX.utils.json_to_sheet(topMoversData);
            XLSX.utils.book_append_sheet(wb, wsTopMovers, 'Top Articles');

            const date = new Date().toISOString().slice(0, 10);
            XLSX.writeFile(wb, `Rapport_Analytique_${date}.xlsx`);

            showToast("Export réussi", "success");
        } catch (error) {
            console.error('Export error:', error);
            showToast("Erreur lors de l'exportation", "error");
        }
    };

    const COLORS = ['#0d6cf2', '#10b981', '#f59e0b', '#94a3b8', '#8b5cf6', '#ec4899'];

    if (loading && !summary) return <div className="p-10 text-center text-slate-500 animate-pulse">Calcul de vos indicateurs de performance...</div>;

    return (
        <>
            {/* Header Section - Sticky */}
            <div className="sticky top-[-16px] lg:top-[-16px] z-30 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-md pt-4 pb-6 mb-2 flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200/50 dark:border-slate-800/50">
                <div>
                    <h1 className="text-3xl font-black tracking-tight bg-gradient-to-r from-primary to-blue-500 bg-clip-text text-transparent drop-shadow-sm">Rapports & Analytique</h1>
                    <p className="text-slate-500 dark:text-slate-400">Analyse détaillée des performances de votre inventaire</p>
                </div>
                {/* 
                <div className="flex items-center gap-3">                   
                    <button
                        onClick={handleExportReport}
                        className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-bold hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                    >
                        <span className="material-symbols-outlined text-lg">download</span>
                        Exporter Rapport
                    </button> 
                    
                    <button className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-bold hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20">
                        Nouveau Scan
                    </button>                    
                </div>
                */}
            </div>

            {/* Time Filter */}
            <div className="mb-8 p-1 bg-slate-200 dark:bg-slate-800 rounded-xl inline-flex w-full max-w-md shadow-inner">
                {['today', 'week', 'month', 'year'].map((p) => (
                    <button
                        key={p}
                        onClick={() => setPeriod(p)}
                        className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${period === p
                            ? 'bg-white dark:bg-slate-900 text-primary shadow-sm'
                            : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
                            }`}
                    >
                        {p === 'today' ? "Aujourd'hui" : p === 'week' ? "Semaine" : p === 'month' ? "Mois" : "Année"}
                    </button>
                ))}
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <KPICard
                    title="Valeur Totale Stock"
                    value={`${Number(summary?.totalStockValue || 0).toLocaleString()} €`}
                    trend={summary?.trends.value}
                    positive={true}
                    icon="payments"
                />
                <KPICard
                    title="Taux de Rotation"
                    value={summary?.turnoverRatio || "0x"}
                    trend={summary?.trends.rotation}
                    positive={true}
                    icon="sync_alt"
                />
                <KPICard
                    title="Articles en Rupture"
                    value={`${summary?.lowStockAlerts || 0} articles`}
                    trend={summary?.trends.alerts}
                    positive={false}
                    icon="warning"
                    isWarning={summary?.lowStockAlerts > 0}
                />
                <KPICard
                    title="Précision d'Inventaire"
                    value={summary?.inventoryPrecision || "100%"}
                    trend={summary?.trends.precision}
                    positive={true}
                    icon="check_circle"
                />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
                {/* Evolution Entrées/Sorties */}
                <div className="lg:col-span-2 p-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
                    <div className="flex items-center justify-between mb-8">
                        <h3 className="font-bold text-lg dark:text-white">Évolution Entrées vs Sorties</h3>
                        <div className="flex gap-4">
                            <div className="flex items-center gap-2">
                                <span className="size-2 rounded-full bg-primary"></span>
                                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Entrées</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="size-2 rounded-full bg-slate-400"></span>
                                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Sorties</span>
                            </div>
                        </div>
                    </div>
                    <div className="h-72 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={chartData.movements} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorEntrees" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#0d6cf2" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#0d6cf2" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                <XAxis
                                    dataKey="date"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fontSize: 10, fontWeight: 700, fill: '#94a3b8' }}
                                    tickFormatter={(val) => {
                                        if (!val) return '';
                                        if (val.includes(':') || val.includes(' ')) return val; // Déjà formaté par le backend (HH:00 ou MMM YY)
                                        try {
                                            const date = new Date(val);
                                            return isNaN(date.getTime()) ? val : date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
                                        } catch (e) {
                                            return val;
                                        }
                                    }}
                                />
                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 700, fill: '#94a3b8' }} />
                                <Tooltip
                                    contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px', color: '#fff' }}
                                    itemStyle={{ fontSize: '12px', fontWeight: 'bold' }}
                                />
                                <Area type="monotone" dataKey="entrees" stroke="#0d6cf2" strokeWidth={3} fillOpacity={1} fill="url(#colorEntrees)" />
                                <Area type="monotone" dataKey="sorties" stroke="#94a3b8" strokeWidth={3} strokeDasharray="5 5" fill="transparent" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Répartition par Catégorie */}
                <div className="p-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
                    <h3 className="font-bold text-lg mb-6 dark:text-white">Valeur par Catégorie</h3>
                    <div className="h-56 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={chartData.categories}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={55}
                                    outerRadius={80}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {chartData.categories.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="space-y-3 mt-4 max-h-32 overflow-y-auto pr-2">
                        {chartData.categories.map((cat, i) => (
                            <div key={cat.name} className="flex items-center justify-between text-xs">
                                <div className="flex items-center gap-2">
                                    <span className="size-2 rounded-full" style={{ backgroundColor: cat.color || COLORS[i % COLORS.length] }}></span>
                                    <span className="text-slate-500 font-medium">{cat.name}</span>
                                </div>
                                <span className="font-bold dark:text-white">
                                    {summary?.totalStockValue > 0 ? ((cat.value / summary.totalStockValue) * 100).toFixed(1) : 0}%
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Top Articles Table */}
            <div className="p-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm mb-10">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="font-bold text-lg dark:text-white">Top Articles les plus mouvementés</h3>
                    <button
                        onClick={() => setShowFullList(true)}
                        className="text-primary text-sm font-bold hover:underline"
                    >
                        Voir tout
                    </button>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="border-b border-slate-100 dark:border-slate-800">
                            <tr>
                                <th className="pb-3 text-xs font-bold text-slate-400 uppercase tracking-wider">Article</th>
                                <th className="pb-3 text-xs font-bold text-slate-400 uppercase tracking-wider">Catégorie</th>
                                <th className="pb-3 text-xs font-bold text-slate-400 uppercase tracking-wider text-center">Mouvements</th>
                                <th className="pb-3 text-xs font-bold text-slate-400 uppercase tracking-wider text-center">Stock Actuel</th>
                                <th className="pb-3 text-xs font-bold text-slate-400 uppercase tracking-wider">Statut</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                            {topMovers.slice(0, 5).map((item) => (
                                <tr key={item.productId} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                    <td className="py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="size-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500">
                                                <span className="material-symbols-outlined text-sm">inventory</span>
                                            </div>
                                            <div>
                                                <p className="font-bold text-sm dark:text-white">{item.name}</p>
                                                <p className="text-[10px] text-slate-500 font-medium">{item.sku}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="py-4 text-sm text-slate-600 dark:text-slate-400">{item.category}</td>
                                    <td className="py-4 text-sm font-black text-center dark:text-white">{item.movements}</td>
                                    <td className="py-4 text-sm text-center dark:text-white">{item.stock} unités</td>
                                    <td className="py-4 text-center">
                                        <span className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${item.status === 'Stable'
                                            ? 'bg-emerald-500/10 text-emerald-500'
                                            : 'bg-rose-500/10 text-rose-500'
                                            }`}>
                                            {item.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Full List Modal */}
            {showFullList && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-900 w-full max-w-4xl max-h-[80vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden">
                        <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                            <div>
                                <h3 className="text-xl font-black dark:text-white">Classement Complet des Articles</h3>
                                <p className="text-sm text-slate-500 font-medium">Basé sur le volume de mouvements ({period})</p>
                            </div>
                            <button
                                onClick={() => setShowFullList(false)}
                                className="size-10 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center transition-colors"
                            >
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 pt-2">
                            <table className="w-full text-left">
                                <thead className="sticky top-0 bg-white dark:bg-slate-900 pb-3 z-10 border-b border-slate-100 dark:border-slate-800">
                                    <tr>
                                        <th className="py-3 text-xs font-bold text-slate-400 uppercase tracking-wider">Article</th>
                                        <th className="py-3 text-xs font-bold text-slate-400 uppercase tracking-wider">Catégorie</th>
                                        <th className="py-3 text-xs font-bold text-slate-400 uppercase tracking-wider text-center">Mouvements</th>
                                        <th className="py-3 text-xs font-bold text-slate-400 uppercase tracking-wider text-center">Stock</th>
                                        <th className="py-3 text-xs font-bold text-slate-400 uppercase tracking-wider">Statut</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                                    {topMovers.map((item) => (
                                        <tr key={item.productId} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                            <td className="py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="size-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500">
                                                        <span className="material-symbols-outlined text-sm">inventory</span>
                                                    </div>
                                                    <div>
                                                        <p className="font-bold text-sm dark:text-white">{item.name}</p>
                                                        <p className="text-[10px] text-slate-500 font-medium">{item.sku}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-4 text-sm text-slate-600 dark:text-slate-400">{item.category}</td>
                                            <td className="py-4 text-sm font-black text-center dark:text-white">{item.movements}</td>
                                            <td className="py-4 text-sm text-center dark:text-white">{item.stock} unités</td>
                                            <td className="py-4">
                                                <span className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${item.status === 'Stable'
                                                    ? 'bg-emerald-500/10 text-emerald-500'
                                                    : 'bg-rose-500/10 text-rose-500'
                                                    }`}>
                                                    {item.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="p-6 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/20 text-right">
                            <button
                                onClick={() => setShowFullList(false)}
                                className="px-6 py-2 bg-slate-200 dark:bg-slate-800 rounded-lg text-sm font-bold hover:bg-slate-300 dark:hover:bg-slate-700 transition-colors"
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

const KPICard = ({ title, value, trend, positive, icon, isWarning }) => (
    <div className="p-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
        <div className="flex items-center justify-between mb-4">
            <div className={`size-10 rounded-xl flex items-center justify-center ${isWarning ? 'bg-rose-500/10 text-rose-500' : 'bg-primary/10 text-primary'}`}>
                <span className="material-symbols-outlined">{icon}</span>
            </div>
            <span className={`text-xs font-black flex items-center gap-0.5 ${positive ? 'text-emerald-500' : 'text-rose-500'}`}>
                <span className="material-symbols-outlined text-sm">{positive ? 'trending_up' : 'trending_down'}</span>
                {trend}
            </span>
        </div>
        <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">{title}</p>
        <p className="text-2xl font-black dark:text-white tracking-tight">{value}</p>
    </div>
);

export default Analytics;
